import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, ArrowRight, Thermometer, AlertTriangle, Info } from 'lucide-react';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Icy Winter Background */}
      <section className="pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 text-white py-20">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50"
              style={{ backgroundImage: "url('/winter/icy-sauna.jpg')" }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/55 via-blue-950/35 to-slate-950/65"></div>
            <div className="relative px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Saunagus booking
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-cyan-100">
                Oplev autentiske saunagus i Vinterbadeklubben - book her
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/sessions"
                  className="bg-white text-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-cyan-50 transition-colors inline-flex items-center"
                >
                  Book Saunagus
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News/Info Box */}
      <section className="py-8 bg-sky-50 border-b border-sky-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-sky-100 border border-sky-300 rounded-lg p-6 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Info className="w-6 h-6 text-sky-600 mt-1" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-sky-800 mb-3">
                  Nyheder og Information
                </h3>
                <div className="text-sky-700 space-y-3">
                  <div className="border-b border-sky-200 pb-3 mb-3">
                    <div className="space-y-2">
                      <p className="font-medium">Velkommen til en 26/27 sæsone</p>
                      <p>
                        Så blev det tid til en ny sæson med gus i vinterbadeklubben. Vi stiller igen i år med at stærkt team af gusmestre - som alle gælder meget sig til at komme igang. Vi har igen 2 gus pr. måned og reglerne for aflysning er de samme som sidste år og de kan ses nederst på denne side.
                      </p>
                      <p className="text-sm mt-2">
                        Mvh Lars
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sauna Experience Section with Photos */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Saunagus for medlemmerne af Vinterbadeklubben
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fordyb dig i saunagus med vores egne Gusmestre.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Autentisk Sauna
              </h3>
              <p className="text-gray-600 mb-6">
                I vinterbadeklubben har vores medlemmer mulighed for at tilmelde sig vores ugentlige saunagus. To gange om ugen -  
                torsdag og søndag afholder vi gus, styret af klubbens egne gusmestre som alle er frivillige og er gusmester i deres fritid.
                Alle gusmestre er forskellige og har hver især deres egen stil. Nogen har en mere sprituel tilgang hvor andre har en meditativ tilgang. 
                Men selvom fokus kan være forskellig fra gusmester til gusmester, vil der altid være nogen faste rammer der går igen i gusen.   

              </p>
              <div className="space-y-3">
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-cyan-600 rounded-full mr-3"></div>
                  <span>3 runder af 12-15 minutter</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-cyan-600 rounded-full mr-3"></div>
                  <span>3 duftopleveser pr runde</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-cyan-600 rounded-full mr-3"></div>
                  <span>En intensivering af varmen gennem runderne</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/winter/frost-window.jpg"
                alt="Udsigt fra saunaen over det frosne vand"
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/30 to-transparent rounded-lg"></div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="/winter/icy-pier.jpg"
                alt="Isbad Oplevelse"
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/30 to-transparent rounded-lg"></div>
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Hvad skal du huske inden du kommer til gus?
              </h3>
              <p className="text-gray-600 mb-6">
                For at få den bedste gus-oplevelse, så skal du huske at:
              </p>
              <div className="space-y-3">
              <div className="flex items-center text-gray-700">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                  <span>Ankomme 15 minutter før gusen starter</span>
                </div>
                <div className="flex items-center text-gray-700">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                  <span>Min 2 håndkælder - et til at sidde på og et til dine fødder</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                  <span>Drikke rigeligt med vækske i timerne op til gusen</span>
                </div>

                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                  <span>Drik ikke alkohol før gusen</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Banner Strip */}
      <section className="relative h-64 overflow-hidden">
        <img
          src="/winter/new-bridge.jpg"
          alt="Broen til vinterbadning"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-blue-950/40 to-slate-950/80"></div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <p className="text-2xl md:text-3xl font-semibold text-white max-w-3xl">
            Fra isen til varmen &ndash; hver torsdag og søndag
          </p>
        </div>
      </section>

      {/* Cancellation Rules Attention Box */}
      <section className="py-8 bg-amber-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-6 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-1" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-amber-800 mb-3">
                  Vigtige regler for afmelding af bookinger og venteliste
                </h3>
                <div className="text-amber-700 space-y-2">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <span><strong>Afmelding mere end 8 timer før gusen:</strong> Du kan aflyse uden problemer. Din månedlige grænse frigives og en person fra ventelisten kan overtage din plads.</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <span><strong>Afmelding mindre end 8 timer før gusen:</strong> Du kan ikke længere aflyse og bruger derfor et klip - pladsen frigives ikke til ventelisten. Du kan ikke overdrage pladsen til andre.</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <span>Hvis du står på venteliste - men allerede har deltaget i 2 gus samme måned, vil du ikke blive tilbudt en plads. </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                    <span><strong>Venteliste ansvar:</strong> Hvis du står på venteliste, er det dit ansvar at tjekke 8 timer før gusen om du har fået en plads. Du bliver ikke automatisk kontaktet.</span>
                  </div>
                </div>
                <p className="text-sm text-amber-600 mt-3 italic">
                  💡 <strong>Tip:</strong> Aflys så tidligt som muligt for at give andre medlemmer mulighed for at overtage din plads!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-cyan-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Fleksibel Tidsplan
              </h3>
              <p className="text-gray-600">
                Husk af afmelde dig hvis du ikke kan komme til gusen.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Uddannede Gusmestre
              </h3>
              <p className="text-gray-600">
                Alle vores gusmestre er uddannede igennem Vinterbadeklubben.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Thermometer className="w-8 h-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sæsonbaseret Tradition
              </h3>
              <p className="text-gray-600">
                Følg den traditionelle sæson fra 1. september til 30. april,
                respekterende årets naturlige rytme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GUS Session Details Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              GUS Session Information
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Alt du skal vide om vores traditionelle GUS sauna sessioner.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-100">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-cyan-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Session Tider
              </h3>
              <p className="text-gray-600">
                Ugentlige sessioner<br />
                20:00 - 21:00
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-blue-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Kapacitet
              </h3>
              <p className="text-gray-600">
                Maksimum 16 personer<br />
                per session
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-100">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-6 h-6 text-teal-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Månedlig Grænse
              </h3>
              <p className="text-gray-600">
                2 sessioner per måned<br />
                per medlem
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-100">
              <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Thermometer className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Sæson
              </h3>
              <p className="text-gray-600">
                1. september<br />
                til 30. april
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/sessions"
              className="bg-blue-800 hover:bg-blue-900 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center"
            >
              Se planen for gus
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section with Icy Background */}
      <section className="py-16 bg-gradient-to-r from-slate-950 via-blue-950 to-cyan-900 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
          style={{ backgroundImage: "url('/winter/icy-bridge.jpg')" }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Klar til Saunagus?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-cyan-100">
            Log ind her.
          </p>
          <Link
            to="/login"
            className="bg-white text-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-cyan-50 transition-colors inline-flex items-center"
          >
            Log Ind
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home; 
